<template>
  <div class="review-transfer-step">
    <!-- Header -->
    <div class="d-flex align-items-center mb-4">
      <!-- <ArgonButton color="white" variant="outline" @click="$emit('back')" class="me-3 back-button">
        <i class="fas fa-chevron-left me-1"></i> Back to Preview Entries
      </ArgonButton> -->
      <h3 class="mb-0 review-title">Review Your Transfer <span class="text-muted entries-count">{{ totalEntries }} Entries</span></h3>
    </div>

    <!-- Transfer Summary Card -->
    <div class="card transfer-summary-card mb-4">
      <div class="card-body">
        <h5 class="card-title mb-3">Transfer Summary</h5>
        <div class="row">
          <div class="col-md-3">
            <div class="summary-item">
              <span class="summary-label">Total Recipient</span>
              <p class="summary-value">{{ summary.totalRecipient }}</p>
            </div>
          </div>
          <div class="col-md-3">
            <div class="summary-item">
              <span class="summary-label">Total Transfer Amount</span>
              <p class="summary-value">IDR {{ formatLocalNumber(summary.totalTransferAmount) }}</p>
            </div>
          </div>
          <div class="col-md-3">
            <div class="summary-item">
              <span class="summary-label">Total Admin Fee</span>
              <p class="summary-value">IDR {{ formatLocalNumber(summary.totalAdminFee) }}</p>
            </div>
          </div>
          <div class="col-md-3">
            <div class="summary-item">
              <span class="summary-label">Batch Name</span>
              <p class="summary-value file-name">
                <i class="fas fa-file-csv me-1"></i>{{ summary.batchName }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <SwiftDataTable
      :headers="tableHeaders" 
      :items="tableData" 
      :items-per-page="itemsPerPage"
      v-model:current-page="currentPage"
      :total-items="totalEntries"
      tableClassName="review-table"
      show-index
      buttons-pagination
      :has-checkbox="false"
    >
      <template #item-amount="{ amount }">
        IDR {{ formatLocalNumber(amount) }}
      </template>
      <template #item-admin_fee="{ admin_fee }">
        IDR {{ formatLocalNumber(admin_fee) }}
      </template>
    </SwiftDataTable>

    <!-- Skipped Errors Alert & Actions -->
    <div v-if="skippedErrorsCount > 0" class="alert error-alert d-flex justify-content-between align-items-center p-3 mt-4" role="alert">
      <div>
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ skippedErrorsCount }} Invalid error were skipped
      </div>
      <ArgonButton color="link" class="download-csv-link" @click="downloadFailedTransactions" :disabled="isDownloadingFailed">
        <span v-if="isDownloadingFailed" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        {{ isDownloadingFailed ? 'Downloading...' : 'Download Invalid CSV' }}
      </ArgonButton>
    </div>

    <!-- Declaration Checkboxes -->
    <div class="declaration-section mt-4 p-4">
      <h6 class="declaration-title mb-3">Declaration</h6>
      <div class="form-check mb-3">
        <input 
          id="declaration-accurate" 
          v-model="declarationAccurate" 
          class="form-check-input declaration-checkbox" 
          type="checkbox"
        />
        <label for="declaration-accurate" class="form-check-label declaration-label">
          I Declare all the information provided is accurate
        </label>
      </div>
      <div class="form-check">
        <input 
          id="declaration-legal" 
          v-model="declarationLegalFunds" 
          class="form-check-input declaration-checkbox" 
          type="checkbox"
        />
        <label for="declaration-legal" class="form-check-label declaration-label">
          I hereby declare that the aforementioned funds are fully and legally my property
        </label>
      </div>
    </div>

    <!-- Bottom Actions -->
    <div class="d-flex justify-content-end align-items-center mt-4 gap-2">
      <ArgonButton 
        variant="outline" 
        @click="$emit('back')" 
        class="me-3 back-button"
      >
        <i class="fas fa-chevron-left me-1"></i> Back to Preview Entries
      </ArgonButton>
      <ArgonButton 
        color="danger" 
        variant="outline"
        @click="onCancelBatch"
      >
        Cancel
      </ArgonButton>
      <ArgonButton color="success" @click="handleConfirm" class="confirm-button">
        Confirm & Continue
      </ArgonButton>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { getBatchSummary, getDataBatchFileFailed, cancelBatch } from '@/api/transfer'; // Add cancelBatch import
import ArgonButton from "@/components/ArgonButton.vue";
import SwiftDataTable from "@/components/SwiftDataTable.vue";
import Swal from 'sweetalert2'; 

const props = defineProps({
  transferData: {
    type: Array,
    default: () => []
  },
  fileName: {
    type: String,
    default: 'Transfer_batch.csv'
  },
  batchUUID: { // New prop for batch UUID
    type: String,
    default: null
  },
  isExistingBatch: { // New prop to indicate if it's an existing batch
    type: Boolean,
    default: false
  }
  // batchSummary prop is removed as summary is fetched internally
});

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['back', 'confirm', 'summary-updated']); // Remove 'balance-updated'

const summary = ref({
  totalRecipient: 0,
  totalTransferAmount: 0,
  totalAdminFee: 0,
  totalAmount: 0,
  batchName: props.fileName
});

// Declaration checkboxes state
const declarationAccurate = ref(false);
const declarationLegalFunds = ref(false);

const tableHeaders = ref([
  { text: 'Account Name', value: 'Account Name', sortable: true },
  { text: 'Bank', value: 'Bank', sortable: true },
  { text: 'Recipient Name', value: 'Account Number', sortable: true },
  { text: 'Amount', value: 'Amount', sortable: true, align: 'right' }, // Assuming Amount is a number
  { text: 'Admin Fee', value: 'Admin Fee', sortable: true, align: 'right' } // Assuming Admin Fee is a number
]);

const tableData = ref([]);
const skippedErrorsCount = ref(0); 
const isDownloadingFailed = ref(false); // For download button loading state

// Pagination
const itemsPerPage = ref(6); // As seen in the image (6 rows per page)
const currentPage = ref(1);

const totalEntries = computed(() => tableData.value.length);

const processTransferData = (data) => {
  if (!data) return;
  let headers = [];
  let dynamicHeaders = [];

  const successData = data.filter(item => item.status === 'Success');
  const failedData = data.filter(item => item.status === 'Failed');
  
  headers = ['account_name', 'account_number', 'amount', 'bank', 'admin_fee']
  dynamicHeaders = headers.map(header => ({
    text: header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), // Format header text
    value: header,
    sortable: true,
  }));

  tableHeaders.value = dynamicHeaders
  tableData.value = successData;
  skippedErrorsCount.value = failedData.length; 

  // Summary will be primarily set by API call in onMounted
  // summary.value.totalRecipient = successData.length;
  // summary.value.totalTransferAmount = successData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  // summary.value.totalAdminFee = successData.reduce((sum, item) => sum + parseFloat(item.admin_fee || 0), 0);
};

// Watcher for props.batchSummary is removed as summary is fetched via API using batchUUID

watch(() => props.transferData, (newData) => {
  processTransferData(newData);
  currentPage.value = 1; 
}, { immediate: true, deep: true });

watch(() => props.fileName, (newName) => {
  summary.value.batchName = newName;
});

const formatLocalNumber = (value) => {
  if (value === null || value === undefined) return '0';
  const num = Number(value);
  if (isNaN(num)) return value; // Return original if not a number (e.g. for error messages)
  return num.toLocaleString('id-ID'); // Using Indonesian locale for formatting like 120.000.000
};

// Validation and confirm handler
const handleConfirm = () => {
  if (!declarationAccurate.value || !declarationLegalFunds.value) {
    Swal.fire({
      title: "Declaration Required",
      html: `
        <p style="text-align: left; margin-bottom: 10px;">Please check the following declarations before continuing:</p>
        <ul style="text-align: left; padding-left: 20px;">
          ${!declarationAccurate.value ? '<li style="color: #dc3545;">✗ I Declare all the information provided is accurate</li>' : '<li style="color: #28a745;">✓ I Declare all the information provided is accurate</li>'}
          ${!declarationLegalFunds.value ? '<li style="color: #dc3545;">✗ I hereby declare that the aforementioned funds are fully and legally my property</li>' : '<li style="color: #28a745;">✓ I hereby declare that the aforementioned funds are fully and legally my property</li>'}
        </ul>
      `,
      icon: "warning",
      confirmButtonText: "OK",
      confirmButtonColor: "#5e72e4"
    });
    return;
  }
  emit('confirm');
};

const downloadFailedTransactions = async () => {
  if (!props.batchUUID) {
    Swal("Error", "Batch ID is not available to download failed transactions.", "error");
    return;
  }
  isDownloadingFailed.value = true;
  try {
    const response = await getDataBatchFileFailed(props.batchUUID);
    if (response instanceof Blob) {
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      const csvFileName = props.fileName ? `failed_entries_${props.fileName.split('.')[0]}.csv` : 'failed_entries.csv';
      link.setAttribute('download', csvFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else if (response && response.status === 'success' && response.data && response.data.file_url) {
      window.open(response.data.file_url, '_blank');
    } else {
      console.error('Failed to download failed transactions:', response);
      Swal("Error", response.message || 'Could not download failed transaction data. The file might be empty or an error occurred.', "error");
    }
  } catch (error) {
    console.error('Error downloading failed transactions:', error);
    Swal("Error", "An error occurred while trying to download failed transactions.", "error");
  } finally {
    isDownloadingFailed.value = false;
  }
};

const onCancelBatch = async () => {
  try {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: props.isExistingBatch 
        ? "You want to cancel this batch transaction?" 
        : "You want to cancel this transfer process?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No, keep it",
    });

    if (result.isConfirmed) {
      if (props.isExistingBatch) {
        // For existing batch, call cancel API
        if (!props.batchUUID) {
          Swal.fire("Error", "Batch ID is not available to cancel.", "error");
          return;
        }

        // Show loading
        Swal.fire({
          title: "Cancelling...",
          text: "Please wait while we cancel the batch.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Call cancel API
        const response = await cancelBatch(props.batchUUID);
        
        if (response && response.status === 'success') {
          Swal.fire({
            title: "Cancelled!",
            text: "Batch has been cancelled successfully.",
            icon: "success",
            confirmButtonText: "OK"
          }).then(() => {
            // Redirect to transaction page
            window.location.href = '/transaction';
          });
        } else {
          throw new Error(response?.message || 'Failed to cancel batch');
        }
      } else {
        // For new upload, just go back to upload step
        Swal.fire({
          title: "Cancelled!",
          text: "Transfer process has been cancelled.",
          icon: "success",
          confirmButtonText: "OK"
        }).then(() => {
          // Navigate back to transfer home or step 1
          window.location.href = '/transfer';
        });
      }
    }
  } catch (error) {
    console.error("Error cancelling batch:", error);
    let errorMessage = "An unexpected error occurred while cancelling.";
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    Swal.fire("Error!", errorMessage, "error");
  }
};

onMounted(async () => {
  // Fetch batch summary if batchUUID is present
  if (props.batchUUID) {
    try {
      const summaryResponse = await getBatchSummary(props.batchUUID);
      if (summaryResponse && summaryResponse.status === 'success' && summaryResponse.data) {
        summary.value.totalRecipient = summaryResponse.data.total_recipient || 0;
        summary.value.totalTransferAmount = parseFloat(summaryResponse.data.total_amount || 0);
        summary.value.totalAdminFee = parseFloat(summaryResponse.data.total_admin_fee || 0);
        summary.value.totalAmount = parseFloat(summaryResponse.data.grand_total_amount || 0);
        emit('summary-updated', summaryResponse.data); // This is key
      } else {
        console.error('ReviewTransferStep: Failed to fetch batch summary:', summaryResponse?.message);
        summary.value.totalRecipient = 0;
        summary.value.totalTransferAmount = 0;
        summary.value.totalAdminFee = 0;
        summary.value.totalAmount = 0;
        emit('summary-updated', null);
      }
    } catch (error) {
      console.error('ReviewTransferStep: Error fetching batch summary:', error);
      summary.value.totalRecipient = 0;
      summary.value.totalTransferAmount = 0;
      summary.value.totalAdminFee = 0;
      summary.value.totalAmount = 0;
      emit('summary-updated', null);
    }
  } else {
     console.warn('ReviewTransferStep: batchUUID is not available on mount. Summary will not be loaded from API.');
     summary.value.totalRecipient = 0;
     summary.value.totalTransferAmount = 0;
     summary.value.totalAdminFee = 0;
    summary.value.totalAmount = 0;
     emit('summary-updated', null);
  }

  // Fallback mock data logic (consider if this is still needed with API calls)
  if (!props.transferData || props.transferData.length === 0) {
      const mockData = [
        { "account_name": "Andi Pratama", bank: "BCA", "account_number": "67218271", amount: 1250000, "admin_fee": 2500, status: 'Success' },
        { "account_name": "Siti Nurhaliza", bank: "BCA", "account_number": "17282817909", amount: 950000, "admin_fee": 2500, status: 'Success' },
        { "account_name": "Error User 1", bank: "Error Bank", "account_number": "ERR001", amount: 100000, "admin_fee": 0, status: 'Failed', error_message: 'Invalid Bank' },
      ];
      processTransferData(mockData);
  }
});

</script>

<style scoped>
.review-transfer-step {
  padding: 20px;
}

.back-button {
  color: #5e72e4; /* Argon primary */
  border-color: #5e72e4;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
}
.back-button:hover {
  background-color: #5e72e4;
  color: white;
}

.review-title {
  color: #344767; /* Default heading color */
  font-weight: 600;
}

.entries-count {
  font-size: 1rem;
  font-weight: 400;
  color: #6c757d; /* Muted text color */
}

.transfer-summary-card {
  border: 1px solid #dee2e6;
  border-radius: 0.75rem; /* Match card style */
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075); /* Subtle shadow */
}

.transfer-summary-card .card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #344767;
}

.summary-item .summary-label {
  display: block;
  font-size: 0.8rem;
  color: #6c757d; /* Muted text */
  margin-bottom: 0.25rem;
}

.summary-item .summary-value {
  font-size: 1rem;
  font-weight: 600;
  color: #344767;
  margin-bottom: 0;
}

.summary-item .file-name {
  color: #0d6efd; /* Bootstrap link blue */
  cursor: default; /* Or pointer if it's clickable */
}
.summary-item .file-name i {
  color: #0d6efd;
}

.review-table :deep(th) {
  background-color: #f8f9fa; /* Light grey for table header */
  color: #6c757d;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: none; /* As per image, not uppercase */
  border-bottom: 1px solid #dee2e6 !important;
}

.review-table :deep(td) {
  font-size: 0.875rem;
  color: #344767;
  vertical-align: middle;
}

.error-alert {
  background-color: #fff3cd; /* Bootstrap warning yellow background */
  border: 1px solid #ffeeba; /* Bootstrap warning yellow border */
  color: #856404; /* Bootstrap warning yellow text */
  font-size: 0.875rem;
  border-radius: 0.375rem;
}

.error-alert i {
  color: #ffc107; /* Bootstrap warning icon color */
}

.download-csv-link {
  color: #007bff; /* Standard link blue */
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
}
.download-csv-link:hover {
  text-decoration: underline;
}

.confirm-button {
  font-weight: 600;
}

/* Declaration Section Styles */
.declaration-section {
  background-color: #f8f9fc;
  border: 1px solid #e3e6f0;
  border-radius: 0.75rem;
}

.declaration-title {
  font-size: 1rem;
  font-weight: 600;
  color: #344767;
  margin-bottom: 1rem;
}

.declaration-checkbox {
  width: 18px;
  height: 18px;
  margin-top: 0.15rem;
  cursor: pointer;
  border: 2px solid #5e72e4;
  border-radius: 4px;
}

.declaration-checkbox:checked {
  background-color: #5e72e4;
  border-color: #5e72e4;
}

.declaration-checkbox:focus {
  box-shadow: 0 0 0 0.2rem rgba(94, 114, 228, 0.25);
  border-color: #5e72e4;
}

.declaration-label {
  font-size: 0.9rem;
  color: #344767;
  cursor: pointer;
  padding-left: 0.5rem;
  line-height: 1.5;
}

/* Styles for SwiftDataTable pagination to match the image */
:deep(.pagination .page-item .page-link) {
  border-radius: 0.375rem !important; /* Rounded corners for pagination items */
  margin: 0 2px;
  border: 1px solid #dee2e6;
  color: #5e72e4; /* Primary color for numbers */
}

:deep(.pagination .page-item.active .page-link) {
  background-color: #5e72e4; /* Primary color for active page */
  border-color: #5e72e4;
  color: white;
}

:deep(.pagination .page-item.disabled .page-link) {
  color: #adb5bd;
  border-color: #dee2e6;
}

:deep(.pagination .page-item:first-child .page-link),
:deep(.pagination .page-item:last-child .page-link) {
  /* Specific styles for prev/next if needed, image shows them similar to numbers */
}

</style>